/**
 * ProjectGroup - Collapsible project section with team list
 * 
 * Shows project header (expand/collapse) with "..." context menu.
 * Drop target for team drag-and-drop between projects.
 */

import React, { useState, useRef } from 'react';
import { TeamListItem } from './TeamListItem';
import { ContextMenu, type MenuAction } from './ContextMenu';
import type { Project } from '../../stores/projectStore';

interface ProjectGroupProps {
  project: Project & { teams?: any[] };
  currentTeamId?: number;
  onSelectTeam: (teamId: number) => void;
  onEditProject: () => void;
  onShareProject: () => void;
  onEditTeam: (teamId: number) => void;
  onShareTeam: (teamId: number) => void;
  onArchiveTeam: (teamId: number) => void;
  onMoveToProject: (teamId: number) => void;
  onTeamDrop: (teamId: number, targetProjectId: number) => void;
  onCreateTeam: () => void;
}

export const ProjectGroup: React.FC<ProjectGroupProps> = ({
  project,
  currentTeamId,
  onSelectTeam,
  onEditProject,
  onShareProject,
  onEditTeam,
  onShareTeam,
  onArchiveTeam,
  onMoveToProject,
  onTeamDrop,
  onCreateTeam,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const teams = (project as any).teams || [];
  const teamCount = teams.length;

  // Context menu actions
  const menuActions: MenuAction[] = [
    { id: 'edit', label: 'Edit Project', icon: '/', onClick: onEditProject },
    { id: 'share', label: 'Share Project', icon: '+', onClick: onShareProject },
    { id: 'add-team', label: 'Add Team', icon: '+', onClick: onCreateTeam, accent: true },
    { id: 'divider', label: '', onClick: () => {} },
    { id: 'archive', label: 'Archive Project', onClick: () => {
      if (window.confirm(`Archive project "${project.name}"?`)) {
        // Handle archive via parent
      }
    }, danger: false, warning: true },
  ];

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const teamId = e.dataTransfer.types.includes('text/team-id');
    if (teamId) {
      setDragOver(true);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const teamId = e.dataTransfer.getData('text/team-id');
    if (teamId) {
      onTeamDrop(parseInt(teamId), project.id);
    }
  };

  return (
    <div className="mb-0.5">
      {/* Project Header */}
      <div
        className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer group transition-colors ${
          dragOver
            ? 'bg-indigo-600/20 border border-indigo-500 border-dashed'
            : 'hover:bg-gray-800/50'
        }`}
        onClick={() => setExpanded(!expanded)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className="text-gray-500 text-[10px] w-3">
          {expanded ? 'v' : '>'}
        </span>
        <span className="text-xs font-semibold flex-1 truncate text-gray-300">
          {project.name}
        </span>
        <span className="bg-gray-700 text-gray-400 text-[10px] px-1.5 py-0.5 rounded">
          {teamCount}
        </span>
        {/* Context menu button - always visible */}
        <button
          ref={menuBtnRef}
          className="text-gray-500 hover:text-gray-300 text-xs px-0.5"
          onClick={(e) => {
            e.stopPropagation();
            const rect = menuBtnRef.current?.getBoundingClientRect() || null;
            setMenuAnchor(rect);
            setShowMenu(!showMenu);
          }}
        >
          ...
        </button>
      </div>

      {/* Context Menu - fixed position */}
      {showMenu && (
        <ContextMenu
          actions={menuActions}
          onClose={() => setShowMenu(false)}
          anchorRect={menuAnchor}
        />
      )}

      {/* Teams List */}
      {expanded && (
        <div className="ml-2">
          {teams.map((team: any) => (
            <TeamListItem
              key={team.id}
              team={team}
              isSelected={currentTeamId === team.id}
              onSelect={() => onSelectTeam(team.id)}
              onEdit={() => onEditTeam(team.id)}
              onShare={() => onShareTeam(team.id)}
              onArchive={() => onArchiveTeam(team.id)}
              onMoveToProject={() => onMoveToProject(team.id)}
              projectId={project.id}
            />
          ))}
          {teams.length === 0 && (
            <div className="px-3 py-2 text-gray-600 text-[10px] italic">
              No teams yet
            </div>
          )}
        </div>
      )}
    </div>
  );
};
